"use client";

import { c as _c } from "react/compiler-runtime";
import * as React from "react";
export interface UseScrollShadowOptions {
  topThreshold?: number;
  bottomThreshold?: number;
  initialCheckDelaysMs?: number[];
}
export interface UseScrollShadowResult {
  scrollRef: (node: HTMLElement | null) => void;
  hasScrolledDown: boolean;
  hasMoreContentBelow: boolean;
}

/**
 * Tracks scroll position of a container and computes whether a top and/or bottom
 * shadow should be shown based on content overflow and current scroll offset.
 */
export function useScrollShadow(options) {
  const $ = _c(13);
  let t0;
  if ($[0] !== options) {
    t0 = options ?? {};
    $[0] = options;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const {
    topThreshold: t1,
    bottomThreshold: t2,
    initialCheckDelaysMs: t3
  } = t0;
  const topThreshold = t1 === undefined ? 5 : t1;
  const bottomThreshold = t2 === undefined ? 5 : t2;
  let t4;
  if ($[2] !== t3) {
    t4 = t3 === undefined ? [100, 300, 500] : t3;
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const initialCheckDelaysMs = t4;
  const containerRef = React.useRef(null);
  const [hasScrolledDown, setHasScrolledDown] = React.useState(false);
  const [hasMoreContentBelow, setHasMoreContentBelow] = React.useState(false);
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = node => {
      containerRef.current = node;
    };
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const scrollRef = t5;
  let t6;
  let t7;
  if ($[5] !== bottomThreshold || $[6] !== initialCheckDelaysMs || $[7] !== topThreshold) {
    t6 = () => {
      const element = containerRef.current;
      if (!element) {
        return;
      }
      const checkScrollState = () => {
        const {
          scrollTop,
          scrollHeight,
          clientHeight
        } = element;
        const scrolledDown = scrollTop > topThreshold;
        const moreBelow = scrollTop + clientHeight < scrollHeight - bottomThreshold;
        setHasScrolledDown(scrolledDown);
        setHasMoreContentBelow(moreBelow);
      };
      for (const delay of initialCheckDelaysMs) {
        setTimeout(checkScrollState, delay);
      }
      element.addEventListener("scroll", checkScrollState, {
        passive: true
      });
      const resizeObserver = new ResizeObserver(() => {
        setTimeout(checkScrollState, 50);
      });
      resizeObserver.observe(element);
      const mutationObserver = new MutationObserver(() => {
        setTimeout(checkScrollState, 100);
      });
      mutationObserver.observe(element, {
        childList: true,
        subtree: true
      });
      return () => {
        element.removeEventListener("scroll", checkScrollState);
        resizeObserver.disconnect();
        mutationObserver.disconnect();
      };
    };
    t7 = [topThreshold, bottomThreshold, initialCheckDelaysMs];
    $[5] = bottomThreshold;
    $[6] = initialCheckDelaysMs;
    $[7] = topThreshold;
    $[8] = t6;
    $[9] = t7;
  } else {
    t6 = $[8];
    t7 = $[9];
  }
  React.useEffect(t6, t7);
  let t8;
  if ($[10] !== hasMoreContentBelow || $[11] !== hasScrolledDown) {
    t8 = {
      scrollRef,
      hasScrolledDown,
      hasMoreContentBelow
    };
    $[10] = hasMoreContentBelow;
    $[11] = hasScrolledDown;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  return t8;
}